import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { LoadingState, EmptyState } from '../../components/ui/Spinner';
import { formatAmount } from '../../lib/format';

interface ExpensesReportProps {
  entityId: string;
  periodId: string;
}

interface CategoryCurrency {
  currency: string;
  total: number;
  daily: Record<string, number>;
}

interface CategoryData {
  category: string;
  currencies: CategoryCurrency[];
}

interface TotalData {
  currency: string;
  total: string;
  daily?: Record<string, number>;
}

interface ExpensesDailyData {
  dates: string[];
  byCategory: CategoryData[];
  totals: TotalData[];
  period: { start: string; end: string };
}

export default function ExpensesReport({ entityId, periodId }: ExpensesReportProps) {
  const { t } = useTranslation('reports');
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = (searchParams.get('viewMode') as 'summary' | 'daily') || 'daily';
  const days = parseInt(searchParams.get('days') || '14', 10) || 14;

  const setViewMode = useCallback((mode: 'summary' | 'daily') => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (mode === 'daily') next.delete('viewMode'); else next.set('viewMode', mode);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setDays = useCallback((d: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (d === 14) next.delete('days'); else next.set('days', String(d));
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const periodParam = periodId ? `&periodId=${periodId}` : '';

  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['report-expenses', entityId, periodId],
    queryFn: async () => {
      const res = await fetch(`/api/reports/expenses?${periodParam.slice(1)}`, {
        headers: { 'X-Entity-Id': entityId },
        credentials: 'include',
      });
      return res.json();
    },
    enabled: viewMode === 'summary',
  });

  const { data: dailyData, isLoading: dailyLoading } = useQuery<ExpensesDailyData>({
    queryKey: ['report-expenses-daily', entityId, periodId, days],
    queryFn: async () => {
      const res = await fetch(`/api/reports/expenses?daily=true&days=${days}${periodParam}`, {
        headers: { 'X-Entity-Id': entityId },
        credentials: 'include',
      });
      return res.json();
    },
    enabled: viewMode === 'daily',
  });

  const isLoading = viewMode === 'summary' ? summaryLoading : dailyLoading;

  if (isLoading) return <LoadingState message={t('expenses.loading')} />;

  return (
    <div className="report-content">
      <div className="card mb-4">
        <div className="controls-row">
          <div className="view-toggle">
            <button
              className={`btn ${viewMode === 'daily' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setViewMode('daily')}
            >
              {t('views.daily')}
            </button>
            <button
              className={`btn ${viewMode === 'summary' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setViewMode('summary')}
            >
              {t('views.summary')}
            </button>
          </div>
          {viewMode === 'daily' && (
            <select
              className="form-input"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              style={{ width: 'auto' }}
            >
              <option value="7">7 {t('balances.days')}</option>
              <option value="14">14 {t('balances.days')}</option>
              <option value="30">30 {t('balances.days')}</option>
              <option value="90">90 {t('balances.days')}</option>
            </select>
          )}
        </div>
      </div>

      {viewMode === 'daily' ? (
        <DailyExpensesTable data={dailyData} />
      ) : (
        <SummaryExpenses data={summaryData} />
      )}

      <style>{`
        .controls-row { display: flex; gap: 16px; align-items: center; flex-wrap: wrap; }
        .view-toggle { display: flex; gap: 8px; }
      `}</style>
    </div>
  );
}

function DailyExpensesTable({ data }: { data: ExpensesDailyData | undefined }) {
  const { t } = useTranslation('reports');

  if (!data?.dates?.length || !data?.byCategory?.length) {
    return (
      <div className="card">
        <EmptyState title={t('expenses.empty.title')} description={t('expenses.empty.description')} />
      </div>
    );
  }

  // Format date for column header (e.g., "15 Dec" or "15 дек")
  const formatDateHeader = (dateStr: string) => {
    const d = new Date(dateStr);
    const day = d.getDate();
    const month = d.toLocaleDateString(undefined, { month: 'short' });
    return `${day} ${month}`;
  };

  // Category colors
  const categoryColors: Record<string, { bg: string; darkBg: string }> = {
    'Operating Expenses': { bg: '#c8e6c9', darkBg: '#2e7d32' },
    'OPEX': { bg: '#c8e6c9', darkBg: '#2e7d32' },
    'Salary': { bg: '#bbdefb', darkBg: '#1565c0' },
    'Payroll': { bg: '#bbdefb', darkBg: '#1565c0' },
    'Taxes': { bg: '#ffe0b2', darkBg: '#ef6c00' },
    'Uncategorized': { bg: '#e0e0e0', darkBg: '#616161' },
  };

  const getColorForCategory = (category: string) => {
    // Check for partial matches
    for (const [key, colors] of Object.entries(categoryColors)) {
      if (category.toLowerCase().includes(key.toLowerCase())) {
        return colors;
      }
    }
    // Default color based on hash
    const hash = category.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const hue = hash % 360;
    return { bg: `hsl(${hue}, 70%, 85%)`, darkBg: `hsl(${hue}, 50%, 35%)` };
  };

  return (
    <div className="card">
      <div className="expenses-table-wrapper">
        <table className="expenses-table">
          <thead>
            <tr>
              <th className="header-category">{t('expenses.title')}</th>
              <th className="header-total">{t('expenses.total')}</th>
              {data.dates.map((date) => (
                <th key={date} className="header-date">{formatDateHeader(date)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.byCategory.map((cat) => {
              const colors = getColorForCategory(cat.category);
              return (
                <>
                  {/* Category Header */}
                  <tr
                    key={`header-${cat.category}`}
                    className="category-header"
                    style={{ '--cat-bg': colors.bg, '--cat-dark-bg': colors.darkBg } as React.CSSProperties}
                  >
                    <td colSpan={2 + data.dates.length}>
                      {cat.category || t('expenses.uncategorized')}
                    </td>
                  </tr>
                  {/* Currency rows under this category */}
                  {cat.currencies.map((curr) => (
                    <tr key={`${cat.category}-${curr.currency}`}>
                      <td className="cell-item">{curr.currency}</td>
                      <td className="cell-total">{formatAmount(curr.total.toString())}</td>
                      {data.dates.map((date) => {
                        const value = curr.daily[date] || 0;
                        return (
                          <td key={date} className="cell-value">
                            {value !== 0 ? formatAmount(value.toString()) : '-'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </>
              );
            })}

            {/* Totals Section */}
            <tr className="totals-header">
              <td colSpan={2 + data.dates.length}>{t('balances.totals')}</td>
            </tr>
            {data.totals.map((tot) => (
              <tr key={`total-${tot.currency}`} className="totals-row">
                <td className="cell-item">{tot.currency}</td>
                <td className="cell-total cell-total-final">{formatAmount(tot.total)}</td>
                {data.dates.map((date) => {
                  const value = tot.daily?.[date] || 0;
                  return (
                    <td key={date} className="cell-value cell-total-value">
                      {value !== 0 ? formatAmount(value.toString()) : '-'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <style>{`
        .expenses-table-wrapper {
          overflow-x: auto;
          overflow-y: visible;
        }

        .expenses-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
          white-space: nowrap;
        }

        .expenses-table th,
        .expenses-table td {
          border: 1px solid var(--color-border);
          padding: 6px 10px;
        }

        .expenses-table thead th {
          background: var(--color-table-header);
          font-weight: 600;
          text-align: center;
        }

        .header-category {
          text-align: left !important;
          min-width: 200px;
        }

        .header-total {
          min-width: 100px;
          background: #fff3e0 !important;
        }

        .header-date {
          min-width: 70px;
        }

        .category-header {
          background: var(--cat-bg) !important;
          font-weight: 600;
        }

        .category-header td {
          padding: 8px 10px;
        }

        .cell-item {
          text-align: left;
          padding-left: 24px !important;
        }

        .cell-total {
          text-align: right;
          font-family: monospace;
          font-weight: 500;
          background: #fff8e1;
          color: var(--color-error);
        }

        .cell-value {
          text-align: right;
          font-family: monospace;
          color: var(--color-error);
        }

        .totals-header {
          background: #ffcdd2 !important;
          font-weight: 600;
        }

        .totals-header td {
          padding: 8px 10px;
        }

        .totals-row {
          background: #ffebee;
        }

        .cell-total-final {
          font-weight: 700;
          font-size: 13px;
        }

        .cell-total-value {
          font-weight: 600;
        }

        /* Dark theme adjustments */
        :root[data-theme="dark"] .category-header {
          background: var(--cat-dark-bg) !important;
          color: white;
        }

        :root[data-theme="dark"] .header-total {
          background: rgba(255, 152, 0, 0.2) !important;
        }

        :root[data-theme="dark"] .cell-total {
          background: rgba(255, 152, 0, 0.1);
        }

        :root[data-theme="dark"] .totals-header {
          background: #c62828 !important;
          color: white;
        }

        :root[data-theme="dark"] .totals-row {
          background: rgba(198, 40, 40, 0.2);
        }
      `}</style>
    </div>
  );
}

function SummaryExpenses({ data }: { data: any }) {
  const { t } = useTranslation('reports');

  if (!data?.byCategory?.length) {
    return (
      <div className="card">
        <EmptyState title={t('expenses.empty.title')} description={t('expenses.empty.description')} />
      </div>
    );
  }

  return (
    <div className="report-content">
      {data?.totals?.length > 0 && (
        <div className="card mb-4">
          <h3 className="card-title mb-4">{t('expenses.total')}</h3>
          <div className="expense-totals">
            {data.totals.map((tot: any) => (
              <div key={tot.currency} className="expense-total">
                <span className="expense-currency">{tot.currency}</span>
                <span className="expense-amount">{formatAmount(tot.total)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <h3 className="card-title mb-4">{t('expenses.byCategory')}</h3>
        <table className="table">
          <thead>
            <tr>
              <th>{t('columns.category')}</th>
              <th>{t('columns.currency')}</th>
              <th className="text-right">{t('columns.txCount')}</th>
              <th className="text-right">{t('cashflow.total')}</th>
            </tr>
          </thead>
          <tbody>
            {data.byCategory.map((b: any, i: number) => (
              <tr key={i}>
                <td><strong>{b.category || 'Uncategorized'}</strong></td>
                <td>{b.currency}</td>
                <td className="text-right">{b.transactionCount}</td>
                <td className="text-right font-mono text-error">{formatAmount(b.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <style>{`
        .expense-totals { display: flex; gap: 32px; }
        .expense-total { display: flex; flex-direction: column; gap: 4px; }
        .expense-currency { font-size: 14px; color: var(--color-gray-500); }
        .expense-amount { font-size: 32px; font-weight: 600; font-family: monospace; color: var(--color-error); }
        .font-mono { font-family: monospace; }
      `}</style>
    </div>
  );
}
